import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import { FormError } from 'shared/components/FormControl';

import InputDatePicker from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentConfigSetting/DateRangeSetting/InputDatePicker';
import { ComponentSettingDateRange } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/types';
import Spacer from 'design-system/components/Spacer';
import { Label } from 'design-system/components/Text';
import {
  InputDateComponentConfig,
  RelativeDate,
  TimePeriodType,
} from 'lib/interfaces/ui-components';

interface DateRangeSettingProps {
  editable: boolean;
  componentSetting: ComponentSettingDateRange;
  componentConfig: InputDateComponentConfig['config'];
  handleUpdateComponentConfig: (componentConfigUpdates: Record<string, any>) => void;
}

const DateRangeSetting = ({
  editable,
  componentConfig,
  componentSetting,
  handleUpdateComponentConfig,
}: DateRangeSettingProps) => {
  const { label } = componentSetting;

  // Returns if current state of date range is invalid
  const isInvalidDateRangeWarning = useMemo(() => {
    const {
      'date-range-start': absoluteStartDate,
      'relative-start-date': relativeStartDate,
      'date-range-end': absoluteEndDate,
      'relative-end-date': relativeEndDate,
    } = componentConfig;

    // Get start and end dates as string
    const startDate = relativeStartDate
      ? DateTime.local()
          .plus({ [relativeStartDate['time-period']]: relativeStartDate['quantity'] })
          .toISO()
      : absoluteStartDate;

    const endDate = relativeEndDate
      ? DateTime.local()
          .plus({ [relativeEndDate['time-period']]: relativeEndDate['quantity'] })
          .toISO()
      : absoluteEndDate;

    if (!startDate || !endDate) {
      return false;
    }
    // Checks if the difference between the end and start date is positive
    if (DateTime.fromISO(endDate).startOf('day') >= DateTime.fromISO(startDate).startOf('day')) {
      return false;
    }
    return true;
  }, [componentConfig]);

  const handleUpdateDateTypeOrValues = useCallback(
    (
      dateType: 'start' | 'end',
      absoluteDateRangeValue: string | null = null,
      relativeDateRangeValue: RelativeDate | null = null,
    ) => {
      const absoluteDateRangeField = `date-range-${dateType}`;
      const relativeDateRangeField = `relative-${dateType}-date`;

      // Check if relative value is there
      if (relativeDateRangeValue) {
        if (
          (relativeDateRangeValue.quantity !== 0 && !relativeDateRangeValue.quantity) ||
          isNaN(relativeDateRangeValue.quantity)
        ) {
          handleUpdateComponentConfig({
            [absoluteDateRangeField]: null,
            [relativeDateRangeField]: null,
          });
        } else {
          handleUpdateComponentConfig({
            [absoluteDateRangeField]: null,
            [relativeDateRangeField]: {
              quantity: relativeDateRangeValue.quantity,
              'time-period': relativeDateRangeValue['time-period']
                ? relativeDateRangeValue['time-period']
                : TimePeriodType.Days,
            },
          });
        }
      } else {
        handleUpdateComponentConfig({
          [absoluteDateRangeField]: absoluteDateRangeValue,
          [relativeDateRangeField]: null,
        });
      }
    },
    [handleUpdateComponentConfig],
  );

  const handleUpdateStartDate = useCallback(
    (absoluteValue: string | null, relativeValue: RelativeDate | null) => {
      handleUpdateDateTypeOrValues('start', absoluteValue, relativeValue);
    },
    [handleUpdateDateTypeOrValues],
  );

  const handleUpdateEndDate = useCallback(
    (absoluteValue: string | null, relativeValue: RelativeDate | null) => {
      handleUpdateDateTypeOrValues('end', absoluteValue, relativeValue);
    },
    [handleUpdateDateTypeOrValues],
  );

  return (
    <div>
      {label && <Label weight='bold'>{label}</Label>}
      <Spacer height={2} />
      <Label size='md'>Start date</Label>
      <InputDatePicker
        onDateChange={handleUpdateStartDate}
        absoluteDate={componentConfig['date-range-start']}
        relativeDate={componentConfig['relative-start-date']}
        disabled={!editable}
      />
      <Spacer height={2} />
      <Label size='md'>End date</Label>
      <InputDatePicker
        onDateChange={handleUpdateEndDate}
        absoluteDate={componentConfig['date-range-end']}
        relativeDate={componentConfig['relative-end-date']}
        disabled={!editable}
      />
      {isInvalidDateRangeWarning && (
        <FormError>
          Warning: There are currently no valid dates between the selected date range
        </FormError>
      )}
    </div>
  );
};

export default DateRangeSetting;
